import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Phone, ArrowDown } from "lucide-react";
import { CONTACT, IMAGES } from "@/lib/content";

// Kinetischer Hero mit Zeile-für-Zeile-Reveal (maskiert) und sanfter Parallax.

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      variants={line}
      custom={i}
      initial="hidden"
      animate="show"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero"
      className="relative lg:min-h-screen pt-24 pb-12 md:pt-32 md:pb-16"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Text */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-soft mb-7"
          >
            <Heart className="w-4 h-4 text-brand-pink" fill="#E06A88" />
            <span className="text-sm font-semibold text-ink-muted">
              Jedes Leben zählt · Igelstation mit Herz
            </span>
          </motion.div>

          <h1 className="font-heading font-extrabold tracking-tight text-ink text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] lg:leading-[1.02]">
            <MaskedLine i={0}>Hallo du</MaskedLine>
            <MaskedLine i={1} className="text-brand-green">lieber Mensch,</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-6 max-w-xl text-lg md:text-xl leading-relaxed text-ink-muted"
          >
            wie schön, dass du dich um einen Igel kümmerst! Als kleine Igelstation
            kümmere ich mich von ganzem Herzen um unsere stacheligen Freunde. Hier
            findest du die wichtigsten Infos, wenn du einen Igel gefunden hast.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/hilfe"
              data-testid="hero-cta-erste-hilfe"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-8 py-4 text-white text-lg font-semibold shadow-soft transition-all duration-300 hover:-translate-y-1 hover:bg-brand-green-dark hover:shadow-card"
            >
              Igel gefunden? Erste Hilfe
            </Link>
            <a
              href={CONTACT.phoneHref}
              data-testid="hero-cta-phone"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-orange px-8 py-4 text-brand-orange text-lg font-semibold transition-all duration-300 hover:-translate-y-1 hover:bg-brand-orange hover:text-white"
            >
              <Phone className="w-5 h-5" /> {CONTACT.phone}
            </a>
          </motion.div>
        </motion.div>

        {/* Bild mit Parallax */}
        <div className="lg:col-span-5 order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[3rem] shadow-card aspect-[4/5]">
              <motion.img
                src={IMAGES.hero}
                alt="Igel zwischen Herbstblättern"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 w-full h-[115%] object-cover"
              />
            </div>
            {/* Deko-Badge */}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: -8 }}
              transition={{ delay: 1, type: "spring", stiffness: 140, damping: 12 }}
              className="absolute -left-4 -bottom-5 md:-left-8 rounded-full bg-brand-pink w-28 h-28 md:w-32 md:h-32 flex items-center justify-center text-center text-white shadow-card"
            >
              <span className="font-heading font-extrabold leading-tight text-sm md:text-base">
                JEDES<br />LEBEN<br />ZÄHLT
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#wobei"
        aria-label="Nach unten scrollen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-ink-muted"
      >
        <span className="text-sm font-medium tracking-wide">mehr erfahren</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          <ArrowDown className="w-5 h-5" />
        </motion.span>
      </motion.a>
    </section>
  );
}
